export abstract class PhoneValidator {
  static readonly validDDDs: number[] = [
    11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28, 31, 32, 33, 34, 35,
    37, 38, 41, 42, 43, 44, 45, 46, 47, 48, 49, 51, 53, 54, 55, 61, 62, 63, 64,
    65, 66, 67, 68, 69, 71, 73, 74, 75, 77, 79, 81, 82, 83, 84, 85, 86, 87, 88,
    89, 91, 92, 93, 94, 95, 96, 97, 98, 99,
  ];

  /**
   * Validates a phone number string based on specific Brazilian phone number rules.
   *
   * The phone number is first cleaned by removing all non-digit characters.
   * It is then validated by applying the following rules:
   *
   * - The cleaned phone number must contain either 10 or 11 digits.
   * - For 11-digit numbers (typically cellphones), the third digit must be "9".
   * - For 10-digit numbers (typically landlines), the third digit must be at least "2"
   *   and one of the allowed values: 2, 3, 4, 5, or 7.
   * - Numbers where all digits are identical are considered invalid.
   * - The area code (first two digits) must be one of the valid DDD values.
   *
   * @param {string} value - The raw phone number string.
   * @returns {boolean} True if the phone number is valid; otherwise, false.
   */
  static isValidPhone(value: string): boolean {
    const cleanedNumber = value.replace(/\D/g, "");

    // Phone number must have 10 or 11 digits.
    if (cleanedNumber.length < 10 || cleanedNumber.length > 11) return false;

    // For 11-digit numbers, the third digit must be "9".
    if (cleanedNumber.length === 11 && cleanedNumber[2] !== "9") return false;

    // For 10-digit numbers, the third digit must be at least "2".
    if (cleanedNumber.length === 10 && Number(cleanedNumber[2]) < 2)
      return false;

    // Reject phone numbers with all identical digits.
    if (/^(\d)\1{9,10}$/.test(cleanedNumber)) return false;

    // Validate the area code (first two digits).
    const ddd = Number(cleanedNumber.substring(0, 2));
    if (!PhoneValidator.validDDDs.includes(ddd)) return false;

    // For 10-digit numbers, the first digit of the local number must be one of [2, 3, 4, 5, 7].
    if (
      cleanedNumber.length === 10 &&
      ![2, 3, 4, 5, 7].includes(Number(cleanedNumber[2]))
    ) {
      return false;
    }

    return true;
  }

  /**
   * Validates whether a given phone number is a valid cellphone number.
   *
   * A valid cellphone number must:
   * - Pass the general phone number validation rules from {@link PhoneValidator.isValidPhone}.
   * - Contain exactly 11 digits.
   * - Have "9" as the third digit (index 2), which is typical for Brazilian cellphones.
   *
   * @param {string} phoneNumber - The raw cellphone number string.
   * @returns {boolean} True if the cellphone number is valid; otherwise, false.
   */
  static isValidCellphone(phoneNumber: string): boolean {
    if (!PhoneValidator.isValidPhone(phoneNumber)) return false;

    const cleanedNumber = phoneNumber.replace(/\D/g, "");
    return cleanedNumber.length === 11 && cleanedNumber[2] === "9";
  }
}

export default PhoneValidator;
